/**
 * Lazy Product Loader
 *
 * Loads pricing-grid rows for individual products on demand via the Shopify
 * Section Rendering API endpoint `product-row-lazy`.
 *
 * Usage
 * -----
 *  initLazyProductLoader({ onProductLoaded })   ← call once after DOM ready
 *  await ensureProductLoaded(handle)            ← call before showing a row
 *  await ensureProductsLoaded([h1, h2, …])      ← batch pre-load
 *  isProductLoaded(handle)                      ← synchronous check
 *  getFamilyOrder()                             ← ordered handle list
 */

import {
  getPinnedHandle,
  reassertPin,
  scheduleDesktopTabindexReassign,
} from './colorPin.js';

const SECTION_NAME = 'product-row-lazy';
const MOBILE_CARD_SECTION_NAME = 'product-mobile-card-lazy';
const SPINNER_DELAY_MS = 50;

/** Handles whose rows have been inserted into the DOM. */
const loadedHandles = new Set<string>();

/** In-flight load promises keyed by handle (deduplicates concurrent requests). */
const pendingLoads = new Map<string, Promise<void>>();

/** Ordered list of all product handles in this family (from window.__productFamily). */
let familyOrder: string[] = [];

/** Optional callback invoked after each product's rows are inserted. */
let onProductLoadedCallback:
  | ((handle: string, $rows: JQuery) => void)
  | undefined;

/** Guards against rendering the blocker notice more than once per page load. */
let blockerBannerShown = false;

/**
 * Handles that deliberately render NO pricing-grid rows: not-released colours
 * published as "Coming Soon".
 *
 * ⚠ Without this, the Coming Soon feature makes the theme lie to customers.
 * `product-inventory-row.liquid` returns nothing for such a product by design,
 * so the lazy fetch succeeds — 200, valid JSON, section present — and then
 * yields zero <tr>. `fetchAndInsert` reads zero rows as "a content blocker ate
 * this request" and shows the ad-blocker banner, which is both false and
 * alarming. Seen live on rn001-navy, 2026-09-18.
 *
 * The set is read from the DOM rather than a `window.*` global on purpose: the
 * global that carries the family list is emitted INSIDE the pricing-grid block,
 * which is itself suppressed on a Coming Soon product's own page — exactly the
 * page where this guard is needed. The swatches always render.
 */
const comingSoonHandles = new Set<string>();

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Initialize the lazy product loader.
 * Must be called once after the DOM is ready.
 *
 * @param options.onProductLoaded  Called with (handle, $rows) after each
 *   successful insertion.  Use this to apply per-row warehouse config that
 *   would normally be done at page-load time (e.g. hide case-qty labels).
 */
export function initLazyProductLoader(options?: {
  onProductLoaded?: (handle: string, $rows: JQuery) => void;
}): void {
  onProductLoadedCallback = options?.onProductLoaded;

  // Read the ordered handle list embedded by Liquid
  const family = window.__productFamily;
  if (Array.isArray(family)) {
    familyOrder = family;
  }

  // Mark products already present in the DOM as loaded
  const seen = new Set<string>();
  $('.warehouse_row').each(function (): void {
    const handle = $(this).attr('data-product-handle');
    if (handle && !seen.has(handle)) {
      seen.add(handle);
      loadedHandles.add(handle);
    }
  });

  // Collect the Coming Soon colours from their swatches — see comingSoonHandles.
  $('.swatch-option[data-coming-soon="true"]').each(function (): void {
    const handle = $(this).attr('data-product_handle');
    if (handle) comingSoonHandles.add(handle);
  });
}

/** Returns true when the given product's rows are already in the DOM. */
export function isProductLoaded(handle: string): boolean {
  return loadedHandles.has(handle);
}

/**
 * Ensures the rows for `handle` are present in the tbody.
 * Resolves immediately if the product is already loaded.
 * Deduplicates concurrent callers — only one network request is made.
 * Shows a "Loading…" overlay if the fetch takes longer than 50 ms.
 */
export async function ensureProductLoaded(handle: string): Promise<void> {
  if (loadedHandles.has(handle)) return;

  // A Coming Soon colour has no grid rows by design. Fetching it would return an
  // empty section, which the failure handling below cannot tell apart from a
  // blocked request — so it would raise the ad-blocker banner. Guarding HERE
  // rather than at each call site is deliberate: this is the single choke point,
  // and the callers are spread across colorFilter, singleColorMode, colorPin,
  // resetFilters, pricingGrid and the deep-link handler in index.ts.
  // Deliberately NOT added to loadedHandles: callers gate on isProductLoaded()
  // before filtering or pinning to a colour, and reporting "loaded" would send
  // them on to act on a colour that has no rows — an empty pricing grid instead
  // of a false banner. Staying "not loaded" makes them skip it, which is right.
  if (comingSoonHandles.has(handle)) return;

  if (pendingLoads.has(handle)) {
    return pendingLoads.get(handle)!;
  }

  const promise = fetchAndInsert(handle);
  pendingLoads.set(handle, promise);
  try {
    await promise;
  } finally {
    pendingLoads.delete(handle);
  }
}

/**
 * Ensures all handles in the array are loaded.
 * Fires requests in parallel.
 */
export async function ensureProductsLoaded(handles: string[]): Promise<void> {
  const unloaded = handles.filter((h) => !loadedHandles.has(h));
  if (unloaded.length === 0) return;
  await Promise.all(unloaded.map((h) => ensureProductLoaded(h)));
}

/** Returns the full ordered list of product handles for this family. */
export function getFamilyOrder(): string[] {
  return familyOrder;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

async function fetchAndInsert(handle: string): Promise<void> {
  const spinnerTimer = setTimeout(() => showSpinner(), SPINNER_DELAY_MS);

  try {
    // Request both sections in one round-trip. Shopify's section rendering
    // API returns each section keyed by name; the mobile card is treated
    // as best-effort — if it's absent the desktop table still loads cleanly.
    const url =
      `/products/${encodeURIComponent(handle)}?sections=` +
      `${SECTION_NAME},${MOBILE_CARD_SECTION_NAME}`;

    let response: Response;
    try {
      response = await fetch(url);
    } catch (error) {
      // fetch() throws on network-layer failures — the dominant cause in the
      // wild is a content/tracking blocker intercepting the request because
      // "/products/...?sections=..." pattern-matches its filter lists.
      // Surface the user-facing notice so they know how to self-recover.
      console.error(
        `[LazyLoader] Network error loading product: ${handle}`,
        error,
      );
      showBlockerBanner();
      return;
    }

    if (!response.ok) {
      console.error(
        `[LazyLoader] HTTP ${response.status} loading product: ${handle}`,
      );
      showBlockerBanner();
      return;
    }

    let data: Record<string, string>;
    try {
      data = (await response.json()) as Record<string, string>;
    } catch (error) {
      // A blocker can substitute a blank/HTML response that fails JSON parse.
      console.error(
        `[LazyLoader] JSON parse failed for product: ${handle}`,
        error,
      );
      showBlockerBanner();
      return;
    }

    const html = data[SECTION_NAME];

    if (!html) {
      console.error(
        `[LazyLoader] No HTML for section "${SECTION_NAME}" (handle: ${handle})`,
      );
      showBlockerBanner();
      return;
    }

    const $rows = insertRows(handle, html);
    if ($rows.length === 0) {
      showBlockerBanner();
      return;
    }

    loadedHandles.add(handle);

    // Mobile card is best-effort — log and continue if absent rather than
    // surfacing the blocker banner, since the desktop table loaded fine.
    const mobileHtml = data[MOBILE_CARD_SECTION_NAME];
    let mobileCardInserted = false;
    if (mobileHtml) {
      try {
        mobileCardInserted = insertMobileCard(handle, mobileHtml);
      } catch (error) {
        console.warn(
          `[LazyLoader] Failed to insert mobile card for ${handle}:`,
          error,
        );
      }
    } else {
      console.warn(
        `[LazyLoader] No HTML for section "${MOBILE_CARD_SECTION_NAME}" (handle: ${handle})`,
      );
    }

    if (!mobileCardInserted) {
      insertFallbackMobileCard(handle, $rows);
    }

    if (onProductLoadedCallback && $rows.length > 0) {
      onProductLoadedCallback(handle, $rows);
    }
  } finally {
    clearTimeout(spinnerTimer);
    hideSpinner();
  }
}

/**
 * Parse the mobile card section HTML and insert the resulting <section>
 * into .mobile-inventory-list, positioned to mirror the table row order.
 *
 * Unlike <tr>, <section> parses cleanly inside a wrapping <div>, so we can
 * skip the table-context trick used for insertRows.
 */
function insertMobileCard(handle: string, html: string): boolean {
  const $list = $('.mobile-inventory-list');
  if ($list.length === 0) {
    // No mobile container on this page (older theme variant) — silently no-op.
    return false;
  }

  // Extract inner content of the shopify-section wrapper:
  //   <div ...class="shopify-section">{CONTENT}</div>
  const openTagEnd = html.indexOf('>') + 1;
  const closeTagStart = html.lastIndexOf('</div>');
  const cardContent =
    openTagEnd > 0 && closeTagStart > openTagEnd
      ? html.slice(openTagEnd, closeTagStart).trim()
      : html.trim();

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = cardContent;
  const cardEl = tempDiv.querySelector('.mobile-inventory-product');
  if (!cardEl) {
    console.warn(
      `[LazyLoader] No .mobile-inventory-product in mobile card response for: ${handle}`,
    );
    return false;
  }

  // De-dupe: if the card was somehow already inserted, replace rather than append.
  const $existing = $list.find(
    `.mobile-inventory-product[data-product-handle="${handle}"]`,
  );
  if ($existing.length > 0) {
    $existing.replaceWith(cardEl);
    return true;
  }

  insertMobileCardInFamilyOrder(handle, cardEl);
  return true;
}

function insertMobileCardInFamilyOrder(handle: string, cardEl: Element): void {
  const $list = $('.mobile-inventory-list');
  const idx = familyOrder.indexOf(handle);
  let $insertAfter: JQuery | null = null;
  for (let i = idx - 1; i >= 0; i--) {
    const prevHandle = familyOrder[i];
    const $prev = $list.find(
      `.mobile-inventory-product[data-product-handle="${prevHandle}"]`,
    );
    if ($prev.length > 0) {
      $insertAfter = $prev.last();
      break;
    }
  }

  if ($insertAfter && $insertAfter.length > 0) {
    $(cardEl).insertAfter($insertAfter);
  } else {
    $list.prepend(cardEl);
  }
}

function appendText(parent: HTMLElement, className: string, text: string): void {
  const span = document.createElement('span');
  span.className = className;
  span.textContent = text;
  parent.appendChild(span);
}

function createFallbackEmptyCell(): HTMLElement {
  const cell = document.createElement('span');
  cell.className = 'mobile-size-cell mobile-size-cell--empty';
  cell.setAttribute('aria-hidden', 'true');
  return cell;
}

function createFallbackSizeCell(cell: HTMLElement, warehouseName: string): HTMLElement {
  const source = cell.querySelector<HTMLInputElement>('.variant_qty_input_directship');
  if (!source?.id) {
    return createFallbackEmptyCell();
  }

  const label = document.createElement('label');
  label.className = 'mobile-size-cell';
  if (cell.classList.contains('no-quantity')) {
    label.classList.add('mobile-size-cell--no-quantity');
  }

  appendText(
    label,
    'mobile-size-cell__size',
    source.dataset.size ?? cell.dataset.variantTitle ?? '',
  );

  const inventoryText =
    cell.querySelector<HTMLElement>('.variant_inventory_label')?.textContent?.trim() ??
    '';
  appendText(label, 'mobile-size-cell__inventory', inventoryText || 'out');

  const caseText =
    cell.querySelector<HTMLElement>('.variant_casePack_label')?.textContent?.trim() ??
    '';
  if (caseText) {
    appendText(label, 'mobile-size-cell__case', caseText);
  }

  const input = document.createElement('input');
  input.className = 'mobile-size-cell__qty';
  input.type = 'text';
  input.inputMode = 'numeric';
  input.pattern = '[0-9]*';
  input.autocomplete = 'off';
  input.setAttribute(
    'aria-label',
    `Quantity for ${source.dataset.size ?? 'size'} at ${warehouseName}`,
  );
  input.dataset.sourceInputId = source.id;
  label.appendChild(input);

  return label;
}

function createWarehouseInfoButton(warehouseName: string): HTMLButtonElement {
  const button = document.createElement('button');
  button.className = 'warehouse-info-btn warehouse-info-btn--mobile';
  button.type = 'button';
  button.setAttribute('aria-label', `Shipping info for ${warehouseName}`);
  button.textContent = 'ⓘ';
  return button;
}

/**
 * Last-resort mobile renderer used when the Section Rendering API returns the
 * canonical table rows but omits/invalidates the mobile-card section. Without
 * this fallback, phone users see no buying surface for the clicked lazy color
 * because the desktop table is hidden at the mobile breakpoint.
 */
function insertFallbackMobileCard(handle: string, $rows: JQuery): void {
  const list = document.querySelector('.mobile-inventory-list');
  const firstRow = $rows.get(0) as HTMLElement | undefined;
  if (!list || !firstRow) return;

  if (list.querySelector(`.mobile-inventory-product[data-product-handle="${handle}"]`)) {
    return;
  }

  const color =
    firstRow.getAttribute('data-inventory-table-color') ??
    firstRow.getAttribute('data-inventory-color') ??
    handle;

  const card = document.createElement('section');
  card.className = 'mobile-inventory-product mobile-inventory-product--fallback';
  card.dataset.productHandle = handle;
  card.dataset.productId = firstRow.dataset.productId ?? '';
  card.dataset.mobileCardColor = color;

  const header = document.createElement('header');
  header.className = 'mobile-inventory-product__header';

  const rowImage = firstRow.querySelector<HTMLImageElement>('.product_image_cell img');
  if (rowImage) {
    const image = document.createElement('img');
    image.className = 'mobile-inventory-product__image';
    image.src = rowImage.currentSrc || rowImage.src;
    image.loading = 'lazy';
    image.width = 60;
    image.height = 60;
    image.alt = rowImage.alt || color;
    header.appendChild(image);
  } else {
    const placeholder = document.createElement('div');
    placeholder.className =
      'mobile-inventory-product__image mobile-inventory-product__image--placeholder';
    header.appendChild(placeholder);
  }

  const meta = document.createElement('div');
  meta.className = 'mobile-inventory-product__meta';
  const colorNode = document.createElement('div');
  colorNode.className = 'mobile-inventory-product__color';
  colorNode.textContent = color;
  const subtotal = document.createElement('div');
  subtotal.className = 'mobile-inventory-product__subtotal';
  subtotal.innerHTML =
    '<span class="mobile-inventory-product__subtotal-qty">0</span>' +
    '<span class="mobile-inventory-product__subtotal-sep">·</span>' +
    '<span class="mobile-inventory-product__subtotal-price">$0.00</span>';
  meta.append(colorNode, subtotal);
  header.appendChild(meta);
  card.appendChild(header);

  const warehouses = document.createElement('div');
  warehouses.className = 'mobile-inventory-product__warehouses';

  $rows.each(function (): void {
    const row = this as HTMLElement;
    const warehouseName = row.dataset.warehouseName ?? 'Warehouse';
    const warehouse = document.createElement('div');
    warehouse.className = 'mobile-inventory-warehouse';
    warehouse.dataset.warehouseCode = row.dataset.warehouseCode ?? '';
    warehouse.dataset.warehouseName = warehouseName;
    warehouse.dataset.warehouseType = row.dataset.warehouseType ?? '';

    const warehouseHeader = document.createElement('div');
    warehouseHeader.className = 'mobile-inventory-warehouse__header';
    appendText(
      warehouseHeader,
      'mobile-inventory-warehouse__name',
      warehouseName,
    );
    warehouseHeader.appendChild(createWarehouseInfoButton(warehouseName));
    if (row.dataset.warehouseRequiresCase === '1') {
      appendText(
        warehouseHeader,
        'mobile-inventory-warehouse__case-required',
        '*Case Purchase Required',
      );
    }
    warehouse.appendChild(warehouseHeader);

    const grid = document.createElement('div');
    grid.className = 'mobile-inventory-size-grid';
    row.querySelectorAll<HTMLElement>('.variant_cell').forEach((variantCell) => {
      if (variantCell.classList.contains('variant_cell_empty')) {
        grid.appendChild(createFallbackEmptyCell());
        return;
      }
      grid.appendChild(createFallbackSizeCell(variantCell, warehouseName));
    });

    warehouse.appendChild(grid);
    warehouses.appendChild(warehouse);
  });

  card.appendChild(warehouses);
  insertMobileCardInFamilyOrder(handle, card);
}

/**
 * Render a dismissible notice above the pricing grid when a lazy product
 * fetch fails. Fires at most once per page load (subsequent failures are
 * silent) so the banner can't stack if the user keeps clicking swatches.
 */
function showBlockerBanner(): void {
  if (blockerBannerShown) return;
  blockerBannerShown = true;

  const $banner = $(
    '<div class="lazy-loader-blocker-banner" role="alert" style="' +
      'margin:0 auto 16px;padding:14px 44px 14px 18px;' +
      'border:2px solid #c23b22;border-radius:6px;' +
      'background:#fff5f3;color:#3a1512;' +
      'font-size:14px;line-height:1.5;position:relative;' +
      'max-width:100%;box-sizing:border-box;' +
    '">' +
      "We couldn't load pricing for some colors. If you use an ad-blocker, " +
      'tracking blocker, or VPN filter, please allow wholesaleprintables.com ' +
      "and reload. We don't really use pop-ups, but it might look like one " +
      'to some filters. Still stuck? Call us at ' +
      '<a href="tel:+12259255789" style="color:inherit;text-decoration:underline;font-weight:600;">(225) 925-5789</a>.' +
      '<button type="button" aria-label="Dismiss" ' +
        'class="lazy-loader-blocker-banner__close" style="' +
        'position:absolute;top:6px;right:10px;background:transparent;' +
        'border:none;font-size:22px;line-height:1;cursor:pointer;' +
        'color:#3a1512;padding:4px 8px;' +
      '">&times;</button>' +
    '</div>',
  );

  $banner.find('.lazy-loader-blocker-banner__close').on('click', function () {
    $banner.remove();
  });

  // Insert above the filter/availability bar so the notice is visible at the
  // top of the pricing area, not buried under the grid. Fall back up through
  // progressively wider parents if the preferred target isn't in the DOM.
  const $filterBar = $('.filter_options_container').first();
  if ($filterBar.length > 0) {
    $banner.insertBefore($filterBar);
    return;
  }

  const $tableGroup = $('.inventory_table_group').first();
  if ($tableGroup.length > 0) {
    $banner.insertBefore($tableGroup);
    return;
  }

  const $wrapper = $('.mobile_table_wrapper').first();
  if ($wrapper.length > 0) {
    $wrapper.prepend($banner);
    return;
  }

  // Last resort: put it somewhere the user will see it even if the grid
  // container is missing entirely.
  $('body').prepend($banner);
}

/**
 * Parse the section HTML and insert the <tr> rows into the tbody in the
 * correct position relative to other loaded products.
 *
 * The Shopify section rendering API wraps output in:
 *   <div id="shopify-section-…" class="shopify-section">{TR CONTENT}</div>
 *
 * We cannot parse <tr> inside a <div> directly: the HTML5 spec says <tr>
 * encountered in "in body" mode (inside a div) is a parse error and is
 * silently IGNORED, so no table row elements are ever created.
 *
 * Instead we extract the inner TR content via string slicing, then parse
 * it inside a proper <table><tbody> wrapper where <tr> is valid.
 */
function insertRows(handle: string, html: string): JQuery {
  // Extract the inner content of the shopify-section wrapper.
  // The wrapper is always: <div ...class="shopify-section">{CONTENT}</div>
  // The first '>' ends the opening tag; the last '</div>' closes the wrapper.
  const openTagEnd = html.indexOf('>') + 1;
  const closeTagStart = html.lastIndexOf('</div>');
  const trContent =
    openTagEnd > 0 && closeTagStart > openTagEnd
      ? html.slice(openTagEnd, closeTagStart).trim()
      : html.trim();

  // Parse inside a valid table context so <tr> elements are created correctly
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = `<table><tbody>${trContent}</tbody></table>`;
  const rowEls = Array.from(tempDiv.querySelectorAll('tr.warehouse_row'));

  if (rowEls.length === 0) {
    console.warn(`[LazyLoader] No .warehouse_row rows found in response for: ${handle}`);
    return $();
  }

  const $newRows = $(rowEls as HTMLElement[]);

  const $tbody = $('.inventory_table_tbody');

  // Insert after the last row of the nearest preceding loaded product.
  //
  // A pinned color (see colorPin.ts) sits at row 1 rather than in its family
  // slot, so it is never a valid anchor: anchoring on it would drop the
  // arriving product at the top of the table instead of its own position.
  // "Show Every Color" pins one color and then loads the entire family, so this
  // fires on essentially every insert on a large product.
  const pinnedHandle = getPinnedHandle();
  const idx = familyOrder.indexOf(handle);
  let $insertAfter: JQuery | null = null;

  for (let i = idx - 1; i >= 0; i--) {
    const prevHandle = familyOrder[i];
    if (prevHandle === pinnedHandle) continue;
    const $prevRows = $(`.warehouse_row[data-product-handle="${prevHandle}"]`);
    if ($prevRows.length > 0) {
      $insertAfter = $prevRows.last();
      break;
    }
  }

  if ($insertAfter && $insertAfter.length > 0) {
    $newRows.insertAfter($insertAfter);
  } else if (pinnedHandle !== null && pinnedHandle !== handle) {
    // Nothing precedes this product in family order, but the pinned group is
    // occupying row 1 — go in directly beneath it rather than above it.
    const $pinnedRows = $(`.warehouse_row[data-product-handle="${pinnedHandle}"]`);
    if ($pinnedRows.length > 0) {
      $newRows.insertAfter($pinnedRows.last());
    } else {
      $newRows.prependTo($tbody);
    }
  } else {
    $newRows.prependTo($tbody);
  }

  // Fix tabindexes — product-row-lazy.liquid always uses product_index:0,
  // so we must reassign based on the product's actual position in familyOrder.
  reassignTabindexes(idx, $newRows);

  // Cheap guarantee that row 1 is still the pinned group, and a debounced
  // rewrite of tabindexes from final DOM order once the inserts settle.
  reassertPin();
  scheduleDesktopTabindexReassign();

  return $newRows;
}

/**
 * Reassign tabindexes on newly inserted rows.
 *
 * product-row-lazy.liquid always renders with product_index:0, giving every
 * lazy-loaded product tabindexes in range 1-20 (same as the first product).
 * We correct this using the product's actual position in familyOrder, mirroring
 * the Liquid formula: base = idx * 100, row offset = rowIdx * 20, variant = +1..n
 */
function reassignTabindexes(familyIdx: number, $rows: JQuery): void {
  if (familyIdx < 0) return;
  const baseTabindex = familyIdx * 100;

  $rows.each(function (rowIdx: number): void {
    $(this).find('.variant_qty_input_directship').each(function (variantIdx: number): void {
      $(this).attr('tabindex', baseTabindex + rowIdx * 20 + variantIdx + 1);
    });
  });
}

function showSpinner(): void {
  if ($('#lazy-load-spinner').length > 0) return;
  $(
    '<div id="lazy-load-spinner"' +
      ' style="position:fixed;top:50%;left:50%;transform:translate(-50%,-50%)' +
      ';z-index:9999;background:rgba(255,255,255,0.9);border-radius:8px' +
      ';padding:18px 28px;box-shadow:0 2px 16px rgba(0,0,0,0.18)' +
      ';font-size:14px;color:#333;">Loading color&hellip;</div>',
  ).appendTo('body');
}

function hideSpinner(): void {
  $('#lazy-load-spinner').remove();
}
